/**
 * Thin wrappers around `posthog-js` for client-side event capture, identity
 * management, and group analytics. All helpers are no-ops when posthog hasn't
 * initialized (consent not granted, API key missing) so call sites can fire
 * unconditionally without `if (initialized)` boilerplate.
 *
 * NEVER pass PHI in `properties` — full names, emails, phone numbers,
 * symptoms, diagnoses, medication names, dates of birth. Use IDs + non-PHI
 * categorical fields only (specialty_code, country, listing_type, etc.).
 */

import posthog from "posthog-js";

function initialized(): boolean {
  if (typeof window === "undefined") return false;
  if (!window.__POSTHOG_INITIALIZED__) return false;
  if (posthog.has_opted_out_capturing?.()) return false;
  return true;
}

/** Capture a custom event. Errors swallowed — analytics MUST NOT break the
 *  product. */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!initialized()) return;
  try {
    posthog.capture(event, properties);
  } catch {
    // swallow
  }
}

/** Fire `$pageview` manually from a router-aware tracker. SPAs need this
 *  because client-side navigation doesn't reload the page. */
export function capturePageview(url: string): void {
  if (!initialized()) return;
  try {
    posthog.capture("$pageview", { $current_url: url });
  } catch {
    // swallow
  }
}

/** Bind the SDK to a stable user identifier and merge non-PHI person
 *  properties. Idempotent — calling with the same id is safe. */
export function identify(distinctId: string, properties?: Record<string, unknown>): void {
  if (!initialized()) return;
  try {
    posthog.identify(distinctId, properties);
  } catch {
    // swallow
  }
}

/** Clear the cached distinct id. Call on logout so the next anonymous
 *  session doesn't inherit the previous user's profile. */
export function reset(): void {
  if (typeof window === "undefined") return;
  if (!window.__POSTHOG_INITIALIZED__) return;
  try {
    posthog.reset();
  } catch {
    // swallow
  }
}

/** Group analytics — attach a group context (provider/facility/business) to
 *  subsequent events on this page so funnels can be sliced by group. */
export function group(
  groupType: string,
  groupKey: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized()) return;
  try {
    posthog.group(groupType, groupKey, properties);
  } catch {
    // swallow
  }
}
